<template>
  <article ref="chartRoot" class="hezabat-report-panel hezabat-report-panel--chart">
    <header class="hezabat-report-panel__head">
      <div>
        <h2 class="hezabat-report-panel__title">{{ $t("reportChartByProduct") }}</h2>
      </div>
    </header>

    <div ref="canvasHost" class="hezabat-report-panel__chart-host">
      <canvas v-show="hasChartData" ref="productChart" class="hezabat-report-panel__canvas" />
      <p v-if="!hasChartData" class="hezabat-report-panel__empty">{{ $t("reportChartNoData") }}</p>
    </div>
  </article>
</template>

<script>
import Chart from "chart.js/auto";

const CHART_GRID = "rgba(148, 163, 184, 0.12)";
const CHART_TICK = "#94a3b8";

export default {
  name: "ReportProductChart",
  props: {
    byProduct: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      chartInstance: null,
      resizeObserver: null,
      renderRetry: 0,
    };
  },
  computed: {
    hasChartData() {
      return this.byProduct.length > 0;
    },
    salesLabel() {
      return this.$t("reportChartSalesCount");
    },
    premiumLabel() {
      return this.$t("reportChartPremiumSum");
    },
  },
  watch: {
    byProduct: {
      deep: true,
      handler() {
        this.$nextTick(() => this.scheduleRender());
      },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scheduleRender();
      this.bindResizeObserver();
    });
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    this.destroyChart();
  },
  methods: {
    scheduleRender() {
      this.renderRetry = 0;
      this.renderChart();
    },
    bindResizeObserver() {
      const host = this.$refs.canvasHost;
      if (!host || typeof ResizeObserver === "undefined") return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.chartInstance) {
          this.chartInstance.resize();
          return;
        }
        this.scheduleRender();
      });
      this.resizeObserver.observe(host);
    },
    destroyChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
        this.chartInstance = null;
      }
    },
    renderChart() {
      if (!this.hasChartData) {
        this.destroyChart();
        return;
      }

      const canvas = this.$refs.productChart;
      const host = this.$refs.canvasHost;
      if (!canvas || !host) return;

      const { width, height } = host.getBoundingClientRect();
      if ((width < 8 || height < 8) && this.renderRetry < 12) {
        this.renderRetry += 1;
        requestAnimationFrame(() => this.renderChart());
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      this.destroyChart();

      const labels = this.byProduct.map((x) => x.productName);
      const sales = this.byProduct.map((x) => Number(x.salesCount || 0));
      const premiums = this.byProduct.map((x) => Number(x.premiumSum || 0));

      this.chartInstance = new Chart(ctx, {
        type: "bar",
        data: {
          labels,
          datasets: [
            {
              label: this.salesLabel,
              data: sales,
              yAxisID: "y",
              borderWidth: 0,
              borderRadius: 8,
              borderSkipped: false,
              backgroundColor: "rgba(56, 189, 248, 0.72)",
              hoverBackgroundColor: "rgba(125, 211, 252, 0.9)",
              maxBarThickness: 36,
            },
            {
              label: this.premiumLabel,
              data: premiums,
              yAxisID: "y1",
              borderWidth: 0,
              borderRadius: 8,
              borderSkipped: false,
              backgroundColor: "rgba(129, 140, 248, 0.62)",
              hoverBackgroundColor: "rgba(165, 180, 252, 0.88)",
              maxBarThickness: 36,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 480 },
          plugins: {
            legend: {
              display: true,
              position: "top",
              align: "end",
              labels: {
                color: "#cbd5e1",
                boxWidth: 10,
                boxHeight: 10,
                padding: 12,
                font: { size: 11, weight: "600" },
                usePointStyle: true,
                pointStyle: "circle",
              },
            },
            tooltip: {
              mode: "index",
              intersect: false,
              backgroundColor: "rgba(15, 23, 42, 0.94)",
              borderColor: "rgba(148, 163, 184, 0.25)",
              borderWidth: 1,
              titleFont: { size: 12, weight: "600" },
              bodyFont: { size: 11 },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                color: CHART_TICK,
                maxRotation: 28,
                minRotation: 0,
                font: { size: 10, weight: "500" },
                autoSkip: true,
                maxTicksLimit: 12,
              },
            },
            y: {
              type: "linear",
              position: "left",
              beginAtZero: true,
              grid: { color: CHART_GRID, borderDash: [3, 4] },
              ticks: { color: CHART_TICK, precision: 0, font: { size: 10 } },
            },
            y1: {
              type: "linear",
              position: "right",
              beginAtZero: true,
              grid: { drawOnChartArea: false },
              ticks: {
                color: "#a5b4fc",
                font: { size: 10 },
                callback: (v) => `${v} ₼`,
              },
            },
          },
        },
      });
    },
  },
};
</script>
